/**
 * SafeDOM.js
 * -------------------------------
 * Extracted DOM utility methods from ContactModal.js
 * Provides safe DOM access and manipulation methods that work in both browser and SSR environments.
 *
 * Features:
 * - SSR-safe DOM operations with automatic browser detection
 * - Error-resistant DOM access methods that gracefully fail
 * - Consistent API for element selection and event management
 * - Optional error tracking integration
 * - Utility methods for common DOM operations
 * - Memory leak prevention for event listeners
 *
 * Use Cases:
 * - Modal components
 * - Form handlers
 * - Dynamic UI components
 * - Any code that needs safe DOM access
 */

export class SafeDOM {
  static isBrowser = typeof window !== 'undefined' &&
                     typeof document !== 'undefined' &&
                     typeof document.getElementById === 'function';

  /**
   * Initialize SafeDOM with optional error handler
   * @param {Object} errorHandler - Optional error handler instance
   */
  static init(errorHandler = null) {
    this.errorHandler = errorHandler;
  }

  /**
   * Safely get element by ID
   * @param {string} id - Element ID
   * @param {Object} options - Options object
   * @param {boolean} options.throwOnNotFound - Throw error if element not found
   * @param {Object} options.errorContext - Additional context for error logging
   * @returns {HTMLElement|null} Element or null if not found/browser not available
   */
  static safeGetElement(id, options = {}) {
    if (!this.isBrowser) return null;
    
    try {
      const element = document.getElementById(id);
      
      if (!element && options.throwOnNotFound) {
        throw new Error(`Element with ID '${id}' not found`);
      }
      
      return element;
    } catch (err) {
      this.errorHandler?.handleError?.('Failed to get DOM element', err, {
        category: 'dom-access',
        elementId: id,
        ...options.errorContext
      });
      
      if (options.throwOnNotFound) {
        throw err;
      }
      
      return null;
    }
  }

  /**
   * Safely query selector
   * @param {string} selector - CSS selector
   * @param {HTMLElement|Document} context - Context element (defaults to document)
   * @param {Object} options - Options object
   * @returns {HTMLElement|null} First matching element or null
   */
  static safeQuerySelector(selector, context = document, options = {}) {
    if (!this.isBrowser) return null;
    
    try {
      const element = context.querySelector(selector);
      
      if (!element && options.throwOnNotFound) {
        throw new Error(`No element found for selector '${selector}'`);
      }
      
      return element;
    } catch (err) {
      this.errorHandler?.handleError?.('Failed to query selector', err, {
        category: 'dom-access',
        selector,
        ...options.errorContext
      });
      
      if (options.throwOnNotFound) {
        throw err;
      }
      
      return null;
    }
  }

  /**
   * Safely query selector all
   * @param {string} selector - CSS selector
   * @param {HTMLElement|Document} context - Context element (defaults to document)
   * @param {Object} options - Options object
   * @returns {Array<HTMLElement>} Array of matching elements (empty array if none found)
   */
  static safeQuerySelectorAll(selector, context = document, options = {}) {
    if (!this.isBrowser) return [];
    
    try {
      const elements = Array.from(context.querySelectorAll(selector));
      
      if (elements.length === 0 && options.throwOnNotFound) {
        throw new Error(`No elements found for selector '${selector}'`);
      }
      
      return elements;
    } catch (err) {
      this.errorHandler?.handleError?.('Failed to query selector all', err, {
        category: 'dom-access',
        selector,
        ...options.errorContext
      });
      
      if (options.throwOnNotFound) {
        throw err;
      }
      
      return [];
    }
  }

  /**
   * Safely add event listener
   * @param {HTMLElement} element - Target element
   * @param {string} event - Event type
   * @param {Function} handler - Event handler function
   * @param {Object|boolean} options - Event listener options
   * @param {Object} context - Additional context for error logging
   * @returns {boolean} True if listener was added successfully
   */
  static safeAddEventListener(element, event, handler, options = {}, context = {}) {
    if (!this.isBrowser || !element?.addEventListener || typeof handler !== 'function') {
      return false;
    }
    
    try {
      element.addEventListener(event, handler, options);
      return true;
    } catch (err) {
      this.errorHandler?.handleError?.('Failed to add event listener', err, {
        category: 'event-management',
        event,
        elementTag: element.tagName,
        elementId: element.id,
        ...context
      });
      return false;
    }
  }

  /**
   * Safely remove event listener
   * @param {HTMLElement} element - Target element
   * @param {string} event - Event type
   * @param {Function} handler - Event handler function
   * @param {Object} context - Additional context for error logging
   * @returns {boolean} True if listener was removed successfully
   */
  static safeRemoveEventListener(element, event, handler, context = {}) {
    if (!this.isBrowser || !element?.removeEventListener || typeof handler !== 'function') {
      return false;
    }
    
    try {
      element.removeEventListener(event, handler);
      return true;
    } catch (err) {
      this.errorHandler?.handleError?.('Failed to remove event listener', err, {
        category: 'event-management',
        event,
        elementTag: element.tagName,
        elementId: element.id,
        ...context
      });
      return false;
    }
  }

  /**
   * Safely set element style
   * @param {HTMLElement} element - Target element
   * @param {string|Object} styles - CSS string or styles object
   * @param {Object} context - Additional context for error logging
   * @returns {boolean} True if styles were applied successfully
   */
  static safeSetStyle(element, styles, context = {}) {
    if (!this.isBrowser || !element?.style) return false;
    
    try {
      if (typeof styles === 'string') {
        element.style.cssText = styles;
      } else if (typeof styles === 'object') {
        Object.assign(element.style, styles);
      }
      return true;
    } catch (err) {
      this.errorHandler?.handleError?.('Failed to set element styles', err, {
        category: 'dom-manipulation',
        elementTag: element.tagName,
        elementId: element.id,
        stylesType: typeof styles,
        ...context
      });
      return false;
    }
  }

  /**
   * Safely add CSS class
   * @param {HTMLElement} element - Target element
   * @param {string} className - Class name to add
   * @param {Object} context - Additional context for error logging
   * @returns {boolean} True if class was added successfully
   */
  static safeAddClass(element, className, context = {}) {
    if (!this.isBrowser || !element?.classList) return false;
    
    try {
      element.classList.add(className);
      return true;
    } catch (err) {
      this.errorHandler?.handleError?.('Failed to add CSS class', err, {
        category: 'dom-manipulation',
        className,
        elementTag: element.tagName,
        elementId: element.id,
        ...context
      });
      return false;
    }
  }

  /**
   * Safely remove CSS class
   * @param {HTMLElement} element - Target element
   * @param {string} className - Class name to remove
   * @param {Object} context - Additional context for error logging
   * @returns {boolean} True if class was removed successfully
   */
  static safeRemoveClass(element, className, context = {}) {
    if (!this.isBrowser || !element?.classList) return false;
    
    try {
      element.classList.remove(className);
      return true;
    } catch (err) {
      this.errorHandler?.handleError?.('Failed to remove CSS class', err, {
        category: 'dom-manipulation',
        className,
        elementTag: element.tagName,
        elementId: element.id,
        ...context
      });
      return false;
    }
  }

  /**
   * Safely toggle CSS class
   * @param {HTMLElement} element - Target element
   * @param {string} className - Class name to toggle
   * @param {boolean} force - Force add (true) or remove (false)
   * @param {Object} context - Additional context for error logging
   * @returns {boolean|null} True if class is now present, false if absent, null on error
   */
  static safeToggleClass(element, className, force = undefined, context = {}) {
    if (!this.isBrowser || !element?.classList) return null;
    
    try {
      return element.classList.toggle(className, force);
    } catch (err) {
      this.errorHandler?.handleError?.('Failed to toggle CSS class', err, {
        category: 'dom-manipulation',
        className,
        force,
        elementTag: element.tagName,
        elementId: element.id,
        ...context
      });
      return null;
    }
  }

  /**
   * Safely focus element
   * @param {HTMLElement} element - Target element
   * @param {Object} options - Focus options
   * @param {number} options.delay - Delay before focusing (ms)
   * @param {boolean} options.preventScroll - Prevent scrolling to element
   * @param {Object} context - Additional context for error logging
   * @returns {boolean} True if focus was successful
   */
  static safeFocus(element, options = {}, context = {}) {
    if (!this.isBrowser || !element?.focus) return false;
    
    const doFocus = () => {
      try {
        element.focus({
          preventScroll: options.preventScroll || false
        });
        return true;
      } catch (err) {
        this.errorHandler?.handleError?.('Failed to focus element', err, {
          category: 'dom-manipulation',
          elementTag: element.tagName,
          elementId: element.id,
          ...context
        });
        return false;
      }
    };
    
    if (options.delay && options.delay > 0) {
      setTimeout(doFocus, options.delay);
      return true; // Assume success for delayed focus
    }
    
    return doFocus();
  }

  /**
   * Safely create element
   * @param {string} tagName - HTML tag name
   * @param {Object} options - Element options
   * @param {string} options.className - CSS class names
   * @param {string} options.id - Element ID
   * @param {string} options.textContent - Text content
   * @param {string} options.innerHTML - HTML content
   * @param {Object} options.attributes - Attributes to set
   * @param {Object} options.styles - Styles to apply
   * @param {Object} context - Additional context for error logging
   * @returns {HTMLElement|null} Created element or null on error
   */
  static safeCreateElement(tagName, options = {}, context = {}) {
    if (!this.isBrowser) return null;
    
    try {
      const element = document.createElement(tagName);
      
      if (options.className) element.className = options.className;
      if (options.id) element.id = options.id;
      if (options.textContent) element.textContent = options.textContent;
      if (options.innerHTML) element.innerHTML = options.innerHTML;
      
      if (options.attributes) {
        Object.entries(options.attributes).forEach(([key, value]) => {
          element.setAttribute(key, value);
        });
      }
      
      if (options.styles) {
        this.safeSetStyle(element, options.styles, context);
      }
      
      return element;
    } catch (err) {
      this.errorHandler?.handleError?.('Failed to create element', err, {
        category: 'dom-manipulation',
        tagName,
        options,
        ...context
      });
      return null;
    }
  }

  /**
   * Safely append child element
   * @param {HTMLElement} parent - Parent element
   * @param {HTMLElement} child - Child element to append
   * @param {Object} context - Additional context for error logging
   * @returns {boolean} True if child was appended successfully
   */
  static safeAppendChild(parent, child, context = {}) {
    if (!this.isBrowser || !parent?.appendChild || !child) return false;
    
    try {
      parent.appendChild(child);
      return true;
    } catch (err) {
      this.errorHandler?.handleError?.('Failed to append child element', err, {
        category: 'dom-manipulation',
        parentTag: parent.tagName,
        parentId: parent.id,
        childTag: child.tagName,
        childId: child.id,
        ...context
      });
      return false;
    }
  }

  /**
   * Safely remove element
   * @param {HTMLElement} element - Element to remove
   * @param {Object} context - Additional context for error logging
   * @returns {boolean} True if element was removed successfully
   */
  static safeRemoveElement(element, context = {}) {
    if (!this.isBrowser || !element?.remove) return false;
    
    try {
      element.remove();
      return true;
    } catch (err) {
      this.errorHandler?.handleError?.('Failed to remove element', err, {
        category: 'dom-manipulation',
        elementTag: element.tagName,
        elementId: element.id,
        ...context
      });
      return false;
    }
  }

  /**
   * Safely get element dimensions
   * @param {HTMLElement} element - Target element
   * @returns {Object|null} Dimensions object or null on error
   */
  static safeGetDimensions(element) {
    if (!this.isBrowser || !element?.getBoundingClientRect) return null;
    
    try {
      const rect = element.getBoundingClientRect();
      return {
        width: rect.width,
        height: rect.height,
        top: rect.top,
        left: rect.left,
        right: rect.right,
        bottom: rect.bottom
      };
    } catch (err) {
      this.errorHandler?.handleError?.('Failed to get element dimensions', err, {
        category: 'dom-access',
        elementTag: element.tagName,
        elementId: element.id
      });
      return null;
    }
  }

  /**
   * Wait for DOM ready state
   * @param {Function} callback - Function to call when DOM is ready
   * @param {number} timeout - Optional timeout in milliseconds
   * @returns {Promise} Promise that resolves when DOM is ready
   */
  static waitForDOMReady(callback = null, timeout = 5000) {
    if (!this.isBrowser) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      if (document.readyState === 'loading') {
        const timeoutId = timeout ? setTimeout(() => {
          reject(new Error('DOM ready timeout'));
        }, timeout) : null;

        const onReady = () => {
          if (timeoutId) clearTimeout(timeoutId);
          document.removeEventListener('DOMContentLoaded', onReady);
          if (callback) callback();
          resolve();
        };

        document.addEventListener('DOMContentLoaded', onReady);
      } else {
        if (callback) callback();
        resolve();
      }
    });
  }

  /**
   * Create an event manager for easier cleanup
   * @returns {Object} Event manager with add, remove, and cleanup methods
   */
  static createEventManager() {
    const listeners = new Map();
    
    return {
      add: (element, event, handler, options = {}) => {
        const success = this.safeAddEventListener(element, event, handler, options);
        if (success) {
          const key = `${element}_${event}`;
          if (!listeners.has(key)) {
            listeners.set(key, []);
          }
          listeners.get(key).push({ handler, options });
        }
        return success;
      },
      
      remove: (element, event, handler) => {
        const success = this.safeRemoveEventListener(element, event, handler);
        if (success) {
          const key = `${element}_${event}`;
          const handlers = listeners.get(key);
          if (handlers) {
            const index = handlers.findIndex(h => h.handler === handler);
            if (index !== -1) {
              handlers.splice(index, 1);
              if (handlers.length === 0) {
                listeners.delete(key);
              }
            }
          }
        }
        return success;
      },
      
      cleanup: () => {
        for (const [key, handlers] of listeners.entries()) {
          const [element, event] = key.split('_');
          handlers.forEach(({ handler }) => {
            this.safeRemoveEventListener(element, event, handler);
          });
        }
        listeners.clear();
      },
      
      getListeners: () => new Map(listeners)
    };
  }
}